import { axios } from '@/lib/axios';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import queryClient from '@/lib/react-query';
import { CallCenterDashboardResponse, featureKeys, featurePath, FeedPauseResponse } from '.';

// feed-pause returns a TOP-LEVEL `{ feed_paused }` body -- the same unwrap
// trap as heartbeat (see sendHeartbeat.ts). No skipErrorToast here: a failed
// explicit pause/resume toggle should surface the global error toast so the
// agent knows the toggle didn't take.
export const putFeedPause = async (paused: boolean): Promise<FeedPauseResponse> => {
  const body = await axios.put<FeedPauseResponse>(`${featurePath}/feed-pause`, { paused });
  return (body as any)?.data ?? (body as unknown as FeedPauseResponse);
};

type FeedPauseMutationContext = {
  previous: CallCenterDashboardResponse | undefined;
};

export const useSetFeedPause = () => {
  return useMutation<FeedPauseResponse, AxiosError, boolean, FeedPauseMutationContext>({
    mutationFn: putFeedPause,
    onMutate: async (paused) => {
      await queryClient.cancelQueries({ queryKey: featureKeys.all });

      const previous = queryClient.getQueryData<CallCenterDashboardResponse>(
        featureKeys.dashboard(),
      );

      queryClient.setQueryData<CallCenterDashboardResponse>(featureKeys.dashboard(), (prev) =>
        prev ? { ...prev, agent: { ...prev.agent, feed_paused: paused } } : prev,
      );

      return { previous };
    },
    onError: (_error, _paused, context) => {
      if (context?.previous) {
        queryClient.setQueryData(featureKeys.dashboard(), context.previous);
      }
    },
    onSuccess: (response) => {
      queryClient.setQueryData<CallCenterDashboardResponse>(featureKeys.dashboard(), (prev) =>
        prev ? { ...prev, agent: { ...prev.agent, feed_paused: response.feed_paused } } : prev,
      );
    },
    // Invalidate on BOTH outcomes. Success: pausing rotates away offered
    // items server-side, so refetch the shrunken queue. Error: onMutate
    // cancelled any in-flight poll and the rollback snapshot may itself be
    // stale, so a refetch reconverges the cache with server truth.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.all });
    },
  });
};
